import {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {DataSectionInitialState, DataSectionSelector} from './typedef';
import type {Error} from 'src/openapi/admin/models/Error';


type Props<Data, ReturnType = void> = {
    selector: DataSectionSelector<Data>,
    saveAction: (data: Data) => Promise<{
        type: string,
        payload: {
            data: ReturnType,
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
    discardAction: () => void,
    saveValidation?: (data: Data) => string | null,
};

export const useSaveDataSection = <Data, ReturnType = void>(params: Props<Data, ReturnType>) => {
    const {
        selector,
        saveAction,
        discardAction,
        saveValidation,
    } = params;

    const {
        isBusy,
        data,
        initialData,
    }: DataSectionInitialState<Data> = useSelector(selector);

    const isChanged = useMemo(() => !isDeepEqual({
        prev: initialData,
        current: data,
    }), [data, initialData]);

    const saveChanges: () => Promise<string | ReturnType | null> = useCallback(async () => {
        let error: string | null = null;
        let result: ReturnType | null = null;

        if (isChanged) {
            if (saveValidation) {
                const saveValidationError = saveValidation(data);
                if (saveValidationError) {
                    return saveValidationError;
                }
            }

            await saveAction(data).then(response => {
                if (response.payload.error) {
                    error = getError({error: response.payload.data, separator: ' '});
                } else if (response.payload.success) {
                    result = response.payload.data;
                }
            });
        }

        return error ? error : result;
    }, [data, isChanged, saveValidation, saveAction]);

    return {
        isBusy,
        isChanged,
        saveChanges,
        discardChanges: discardAction,
    };
};
